import { canRead, permittedRead } from '@/plugins/http/permittedRead'
import { ROLE_TEXT } from '@/utils/adminRole'
import {
  CALLPOP_ACTION,
  OPERATOR_LIST_READ,
  itemsOf,
  operatorCapabilitiesOf,
  operatorOptionsOf,
  operatorWriteAbility,
} from '@/utils/callpopOrg'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'
import { latestGuard } from '@/utils/latestGuard'

/**
 * **「이 세션이 볼 수 있는 운영사」를 읽는 한 곳** — 네 화면이 같은 목록을 쓴다.
 *
 * 계정 발급(OPERATOR 축) · 운영사·관리점 관리(왼쪽 목록) · 매장 입점 현황(필터·이관 목적지) ·
 * 변경 이력(대상 지목)이 전부 `GET /callpop/operators` 하나를 읽는다. 화면마다 적으면 **액션 게이트를
 * 한 곳에서 빠뜨리는 날**이 오고, 그 화면의 첫 진입이 401 → 세션 종료가 된다(서버 보고서 §10 3).
 *
 * 범위는 서버가 좁힌다 — 일반 운영사는 자기 1행, 관리점은 자기 운영사 1행이다(§3-1). 화면은 받은 것을 그린다.
 *
 * **쓰기 자격도 같은 응답이 준다**(§2-2 `capabilities`) — 그래서 버튼 판정([operatorAbility])의 집도 여기다.
 *
 * **응답의 순서와 로딩도 여기가 정한다**(R-G 교차 리뷰 4). 조건을 바꿔 다시 읽는 화면(운영사·관리점 관리의
 * 「비활성 포함」)에서는 조회가 겹치고, 늦게 온 앞 회차가 이기면 꺼진 조건의 목록·자격이 다시 선다. 그래서 반영과
 * 로딩 종료를 **최신 회차만** 한다(`utils/latestGuard`) — 화면이 로딩 칸을 따로 들면 앞 회차의 종료가 최신의
 * 로딩을 거둔다. 한 번만 부르는 화면(계정 관리·매장 입점 현황·변경 이력)은 언제나 최신이라 달라지는 것이 없다.
 *
 * ```js
 * mixins: [mainVueMixin, callpopOperatorsMixin],
 * created() { this.loadOperators() },
 * ```
 */
export default {
  data: function () {
    return {
      /** 서버가 준 운영사 행 그대로 — 관리점 수·매장 수·상태를 쓰는 화면이 읽는다 */
      operatorRows: [],
      /** 셀렉트가 읽는 모양(`{id, label, operatorCd, code, name, hqYn, useYn}`) */
      operatorOptions: [],
      /**
       * 같은 응답이 준 능력 값(§2-2). **`null` 은 「아직 모른다」**(안 읽었거나 못 읽었다)이고, 그동안
       * [operatorAbility] 는 마스터 쓰기를 전부 닫는다 — 「모른다」를 「허용」으로 접으면 첫 조회가 끝나기
       * 전 구간에 거짓 버튼이 선다(일반 운영사도 `CallpopOperatorAdd` 액션을 갖는다).
       */
      operatorCapabilities: null,
      /** 못 읽은 이유 — 빈 문자열이면 정상이다 */
      operatorReadDenied: '',
      /** 최신 회차가 아직 도는가 — 앞 회차의 종료는 이 칸을 건드리지 않는다 */
      operatorLoading: false,
      /** 운영사 조회의 회차 — 늦게 온 앞 회차가 최신의 목록·자격·로딩을 덮지 않게 */
      operatorGuard: latestGuard(),
    }
  },
  // 떠난 화면의 조회는 버린다 — 늦은 응답이 고른 행·관리점 조회로 이어지지 않게
  beforeDestroy: function () {
    this.operatorGuard.close()
  },
  computed: {
    /**
     * 운영사 목록을 **못 읽는 이유** — 빈 문자열이면 정상이다.
     *
     * 게이트는 조회를 소유한 `permittedRead` 와 같은 술어([canRead])를 읽는다. 화면이 `sa_actions` 를
     * 따로 세면 게이트가 둘이 되고, 한쪽만 고쳐질 때 칸은 열려 있는데 조회는 못 하는 상태가 된다.
     */
    operatorsDenied: function () {
      if (!canRead(OPERATOR_LIST_READ)) return ROLE_TEXT.scopeReadDenied

      return this.operatorReadDenied
    },
    /**
     * **운영사 마스터 쓰기 버튼의 자격 한 곳** — 세션 액션과 서버 능력 값의 곱이다(§2-2).
     *
     * 목록을 읽는 자가 능력 값도 받으므로 판정도 여기 있다. 화면·컴포넌트가 각자 곱하면 한쪽만 고쳐질 때
     * 버튼은 서는데 저장이 403 이 되는 상태가 생긴다 — [operatorsDenied] 가 게이트를 하나로 둔 것과 같은 이유다.
     *
     * 총괄 축은 능력 값(`designateHq`)만 내보내고 **곱할 액션은 쓰는 쪽이 고른다** — 조작마다 리소스가
     * 다르기 때문이다(총괄 행 비활성 = `off`, 등록 폼의 칸 = `add`, 수정 폼의 칸 = `up`).
     */
    operatorAbility: function () {
      return operatorWriteAbility(
        {
          add: checkAuthActionAccess(CALLPOP_ACTION.OPERATOR_ADD),
          up: checkAuthActionAccess(CALLPOP_ACTION.OPERATOR_UP),
          off: checkAuthActionAccess(CALLPOP_ACTION.OPERATOR_OFF),
          on: checkAuthActionAccess(CALLPOP_ACTION.OPERATOR_ON),
          waive: checkAuthActionAccess(CALLPOP_ACTION.OPERATOR_WAIVE),
        },
        this.operatorCapabilities
      )
    },
  },
  methods: {
    /**
     * 운영사 목록을 읽는다 — **호출부는 `await` 하지 않아도 된다**(403 은 값으로 접고 그 밖의 실패는
     * 여기서 끝낸다). 액션이 없으면 요청 자체를 보내지 않는다.
     *
     * **최신 회차만 반영한다**(머리말): 그 사이 다음 조회가 나갔으면 이 응답은 목록·자격·로딩 어느 것도 건드리지
     * 않고 거짓으로 끝난다 — 호출부가 「읽었다」 뒤에 잇는 일(고른 행 유지·관리점 조회)도 최신 회차만 한다.
     *
     * @param {?object} params `useYn` 같은 질의 조건
     * @returns {Promise<boolean>} 이 회차를 반영했는가 — 못 읽었거나 낡았으면 거짓
     */
    loadOperators: async function (params) {
      if (this.operatorsDenied) return false

      const seq = this.operatorGuard.begin()
      this.operatorLoading = true

      try {
        const outcome = await permittedRead(Object.assign({}, OPERATOR_LIST_READ, params ? { params } : null))
        if (!this.operatorGuard.isLatest(seq)) return false

        if (!outcome.allowed) {
          this.operatorReadDenied = outcome.message || ROLE_TEXT.scopeReadDenied
          // 못 읽은 뒤에 앞 회차의 능력 값을 들고 있으면 그 버튼은 근거 없이 선다
          this.operatorCapabilities = null

          return false
        }

        this.operatorReadDenied = ''
        this.operatorRows = itemsOf(outcome.data)
        this.operatorOptions = operatorOptionsOf(this.operatorRows)
        this.operatorCapabilities = operatorCapabilitiesOf(outcome.data)

        return true
      } catch (error) {
        // 403 이 아닌 실패 — 인터셉터가 이미 말했다. 목록은 그대로 두고 값으로 끝낸다.
        // 자격만은 버린다: 실패한 조회는 「지금도 쓸 수 있다」의 근거가 되지 못한다(낡은 회차의 실패는 말할 자격이 없다)
        if (this.operatorGuard.isLatest(seq)) this.operatorCapabilities = null

        return false
      } finally {
        if (this.operatorGuard.isLatest(seq)) this.operatorLoading = false
      }
    },
  },
}
